import { bindable, BindingMode, customElement } from 'aurelia';
import template from './sort-select.html';

interface SortChoice {
  value: string;
  labelKey: string;
  icon: string;
  id: string;
}

let nextSortSelectId = 0;

@customElement({ name: 'sort-select', template })
export class SortSelect {
  @bindable({ mode: BindingMode.twoWay }) public value = 'capturedat:desc';
  @bindable public disabled = false;
  @bindable public compact = false;
  @bindable public variant: 'media' | 'collections' = 'media';

  public isOpen = false;
  public highlightedIndex = -1;
  public hostEl: HTMLElement | null = null;

  private readonly mediaOptions: SortChoice[] = [
    {
      value: 'capturedat:desc',
      labelKey: 'gallery.sort.date',
      icon: 'fa-arrow-down-wide-short',
      id: 'captured-desc',
    },
    {
      value: 'capturedat:asc',
      labelKey: 'gallery.sort.date',
      icon: 'fa-arrow-up-wide-short',
      id: 'captured-asc',
    },
    {
      value: 'uploadedat:desc',
      labelKey: 'gallery.sort.uploaded',
      icon: 'fa-arrow-down-wide-short',
      id: 'uploaded-desc',
    },
    {
      value: 'uploadedat:asc',
      labelKey: 'gallery.sort.uploaded',
      icon: 'fa-arrow-up-wide-short',
      id: 'uploaded-asc',
    },
    {
      value: 'title:asc',
      labelKey: 'gallery.sort.title',
      icon: 'fa-arrow-down-a-z',
      id: 'title-asc',
    },
  ];

  private readonly collectionOptions: SortChoice[] = [
    {
      value: 'capturedat:desc',
      labelKey: 'collections.picker.sortDateDescending',
      icon: 'fa-arrow-down-wide-short',
      id: 'collection-date-desc',
    },
    {
      value: 'capturedat:asc',
      labelKey: 'collections.picker.sortDateAscending',
      icon: 'fa-arrow-up-wide-short',
      id: 'collection-date-asc',
    },
    {
      value: 'title:asc',
      labelKey: 'collections.picker.sortAlphabetical',
      icon: 'fa-arrow-down-a-z',
      id: 'collection-title-asc',
    },
  ];

  public get options(): SortChoice[] {
    return this.variant === 'collections'
      ? this.collectionOptions
      : this.mediaOptions;
  }

  public get ariaLabelKey(): string {
    return this.variant === 'collections'
      ? 'collections.picker.sortLabel'
      : 'gallery.filters.sort';
  }

  private readonly menuId = `sort-select-options-${++nextSortSelectId}`;
  private readonly _documentClickHandler = (event: MouseEvent): void => {
    const target = event.target;
    if (target instanceof Node && this.hostEl?.contains(target)) return;
    this.closeDropdown();
  };

  public get selectedOption(): SortChoice {
    return this.options.find(option => option.value === this.value) ?? this.options[0];
  }

  public get activeOptionId(): string | null {
    if (!this.isOpen || this.highlightedIndex < 0) return null;
    return `sort-option-${this.options[this.highlightedIndex].id}`;
  }

  public attached(): void {
    document.addEventListener('click', this._documentClickHandler);
  }

  public detaching(): void {
    document.removeEventListener('click', this._documentClickHandler);
  }

  public toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    if (this.disabled) return;
    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  public onTriggerKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      this.closeDropdown();
      return;
    }
    if (this.disabled) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveHighlight(1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveHighlight(-1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this.openDropdown();
      this.highlightedIndex = 0;
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      this.openDropdown();
      this.highlightedIndex = this.options.length - 1;
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!this.isOpen) {
        this.openDropdown();
      } else {
        this.selectOption(this.options[this.highlightedIndex] ?? this.selectedOption);
      }
      return;
    }
    if (event.key === 'Escape') {
      if (this.isOpen) {
        event.preventDefault();
        this.closeDropdown();
      }
    }
  }

  public selectOption(option: SortChoice, event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.disabled) return;
    this.value = option.value;
    this.closeDropdown();
  }

  private openDropdown(): void {
    if (this.disabled) return;
    this.isOpen = true;
    this.highlightedIndex = this.options.findIndex(option => option.value === this.value);
    if (this.highlightedIndex < 0) this.highlightedIndex = 0;
  }

  private closeDropdown(): void {
    this.isOpen = false;
    this.highlightedIndex = -1;
  }

  private moveHighlight(delta: number): void {
    const start = this.isOpen
      ? this.highlightedIndex
      : this.options.findIndex(option => option.value === this.value);
    this.isOpen = true;
    this.highlightedIndex = Math.max(0, Math.min(start + delta, this.options.length - 1));
  }
}
